/**
 * FOIA tab: search government agency documents and open in a reader (post-like).
 */
import {useCallback, useState} from 'react'
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  View,
} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {useMemo} from 'react'
import {useOpenLink} from '#/lib/hooks/useOpenLink'
import {useFOIAAgenciesQuery, useFOIASearchQuery, type FOIAResult} from '#/state/queries/foia'
import {atoms as a, useTheme, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {PageText_Stroke2_Corner0_Rounded as DocumentIcon} from '#/components/icons/PageText'
import {MagnifyingGlass_Stroke2_Corner0_Rounded as SearchIcon} from '#/components/icons/MagnifyingGlass'
import * as Select from '#/components/Select'
import {Text} from '#/components/Typography'
import * as TextField from '#/components/forms/TextField'
import {Link} from '#/components/Link'

export function FOIATab() {
  const {_} = useLingui()
  const t = useTheme()
  const openLink = useOpenLink()
  const [query, setQuery] = useState('')
  const ALL_AGENCIES_VALUE = '__all__'
  const [agencyId, setAgencyId] = useState<string>(ALL_AGENCIES_VALUE)
  const [selectedDoc, setSelectedDoc] = useState<FOIAResult | null>(null)
  const docDialogControl = Dialog.useDialogControl()

  const {data: agencies = []} = useFOIAAgenciesQuery()
  const agencyItems = useMemo(
    () => [
      {value: ALL_AGENCIES_VALUE, label: _(msg`All agencies`)},
      ...agencies.map(a => ({
        value: a.id,
        label: a.abbreviation ? `${a.name} (${a.abbreviation})` : a.name,
      })),
    ],
    [agencies, _],
  )
  const selectedAgency = agencyItems.find(i => i.value === agencyId)
  const effectiveAgencyId =
    agencyId === ALL_AGENCIES_VALUE || !agencyId ? null : agencyId

  const {data: results = [], isLoading, isFetching, error} = useFOIASearchQuery(
    query,
    effectiveAgencyId,
  )

  const onOpenDocument = useCallback(
    (doc: FOIAResult) => {
      const url = doc.sourceUrl
      if (url) {
        openLink(url)
      } else {
        setSelectedDoc(doc)
        docDialogControl.open()
      }
    },
    [docDialogControl, openLink],
  )

  const onCloseDocument = useCallback(() => {
    docDialogControl.close()
  }, [docDialogControl])

  return (
    <>
      <View
        style={[
          a.flex_1,
          a.px_lg,
          a.pt_md,
          a.pb_lg,
          web({maxWidth: 600, alignSelf: 'center', width: '100%'}),
        ]}>
        <View style={[a.pb_md, a.gap_md]}>
          <Select.Root value={agencyId} onValueChange={setAgencyId}>
            <Select.Trigger label={_(msg`Filter by agency`)}>
              <Select.ValueText
                placeholder={_(msg`All agencies`)}
                webOverrideValue={selectedAgency}>
                {item => (item ? item.label : _(msg`All agencies`))}
              </Select.ValueText>
              <Select.Icon />
            </Select.Trigger>
            <Select.Content
              label={_(msg`Agency`)}
              items={agencyItems}
              renderItem={item => (
                <Select.Item key={item.value} value={item.value} label={item.label}>
                  <Select.ItemIndicator />
                  <Select.ItemText>{item.label}</Select.ItemText>
                </Select.Item>
              )}
            />
          </Select.Root>
          <TextField.Root>
            <TextField.Icon icon={SearchIcon} />
            <TextField.Input
              label={_(msg`Search FOIA documents`)}
              placeholder={_(msg`Search agencies or keywords…`)}
              value={query}
              onChangeText={setQuery}
              returnKeyType="search"
              autoCorrect={false}
              autoCapitalize="none"
            />
          </TextField.Root>
        </View>

        {query.trim() === '' ? (
          <View style={[a.flex_1, a.justify_center, a.align_center, a.gap_md]}>
            <DocumentIcon
              width={48}
              height={48}
              fill={t.palette.primary_500}
              aria-hidden
            />
            <Text style={[a.text_lg, a.font_semi_bold, t.atoms.text]}>
              <Trans>FOIA documents</Trans>
            </Text>
            <Text
              style={[a.text_md, t.atoms.text_contrast_medium, a.text_center, {maxWidth: 320}]}
              numberOfLines={0}>
              <Trans>Search for documents from U.S. government agencies released under the Freedom of Information Act.</Trans>
            </Text>
          </View>
        ) : isLoading || (isFetching && results.length === 0) ? (
          <View style={[a.flex_1, a.justify_center, a.align_center]}>
            <ActivityIndicator size="large" />
            <Text style={[a.pt_md, a.text_md, t.atoms.text_contrast_medium]}>
              <Trans>Searching…</Trans>
            </Text>
          </View>
        ) : error ? (
          <View style={[a.flex_1, a.justify_center, a.p_md]}>
            <Text style={[a.text_md, t.palette.negative_500]}>
              <Trans>Search failed. Try again.</Trans>
            </Text>
          </View>
        ) : results.length === 0 ? (
          <View style={[a.flex_1, a.justify_center, a.align_center, a.gap_sm]}>
            <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
              <Trans>No results for &quot;{query}&quot;</Trans>
            </Text>
          </View>
        ) : (
          <FlatList
            data={results}
            keyExtractor={item => item.id}
            contentContainerStyle={[a.pb_xl]}
            renderItem={({item}) => (
              <FOIAResultRow doc={item} onPress={() => onOpenDocument(item)} />
            )}
          />
        )}
      </View>

      <Dialog.Outer control={docDialogControl} onClose={() => setSelectedDoc(null)}>
        <Dialog.Handle />
        <Dialog.ScrollableInner
          label={selectedDoc?.title ?? _(msg`FOIA document`)}
          style={[web({maxWidth: 560})]}>
          <Dialog.Close />
          {selectedDoc ? (
            <FOIADocumentContent document={selectedDoc} onClose={onCloseDocument} />
          ) : null}
        </Dialog.ScrollableInner>
      </Dialog.Outer>
    </>
  )
}

function FOIAResultRow({
  doc,
  onPress,
}: {
  doc: FOIAResult
  onPress: () => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  return (
    <Pressable
      onPress={onPress}
      style={[
        a.p_md,
        a.rounded_md,
        a.mb_sm,
        t.atoms.bg_contrast_25,
        web({cursor: 'pointer'}),
      ]}
      accessibilityRole="button"
      accessibilityLabel={doc.title}
      accessibilityHint={_(msg`Opens link to document`)}>
      <Text style={[a.text_md, a.font_semi_bold, t.atoms.text]} numberOfLines={2}>
        {doc.title}
      </Text>
      <Text style={[a.text_sm, t.atoms.text_contrast_medium, a.pt_xs]}>
        {doc.agency}
        {doc.date ? ` · ${doc.date}` : ''}
      </Text>
      {doc.snippet ? (
        <Text
          style={[a.text_sm, t.atoms.text_contrast_medium, a.pt_xs]}
          numberOfLines={2}>
          {doc.snippet}
        </Text>
      ) : null}
    </Pressable>
  )
}

function FOIADocumentContent({
  document: doc,
  onClose,
}: {
  document: FOIAResult
  onClose: () => void
}) {
  const {_} = useLingui()
  const t = useTheme()

  return (
    <View style={[a.px_lg, a.pt_md, a.pb_2xl]}>
      <Text style={[a.text_2xl, a.font_bold, t.atoms.text, a.pb_sm]}>
        {doc.title}
      </Text>
      <View style={[a.flex_row, a.flex_wrap, a.gap_sm, a.pb_md]}>
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
          {doc.agency}
          {doc.agencyAbbreviation ? ` (${doc.agencyAbbreviation})` : ''}
        </Text>
        {doc.date ? (
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>{doc.date}</Text>
        ) : null}
      </View>
      <View style={[a.border_t, t.atoms.border_contrast_low, a.pt_md]}>
        <Text
          style={[a.text_md, a.leading_relaxed, t.atoms.text]}
          selectable>
          {doc.body}
        </Text>
      </View>
      {doc.sourceUrl ? (
        <View style={[a.pt_lg]}>
          <Link
            href={doc.sourceUrl}
            style={[a.text_md, t.palette.primary_500]}
            external>
            <Trans>View on source site</Trans>
          </Link>
        </View>
      ) : null}
      <View style={[a.pt_xl]}>
        <Button
          label={_(msg`Close`)}
          color="secondary"
          size="large"
          onPress={onClose}>
          <ButtonText>
            <Trans>Close</Trans>
          </ButtonText>
        </Button>
      </View>
    </View>
  )
}
